import React from 'react';
import type { NodeTypeInfo, WorkflowDef } from '../types';

interface SidebarProps {
  nodeTypes: Record<string, NodeTypeInfo>;
  workflow: WorkflowDef | null;
  onSave: (wf: WorkflowDef) => void;
  onShowTour?: () => void;
  requestedTab?: 'nodes' | 'info' | null;
}

export default function Sidebar({
  nodeTypes,
  workflow,
  onSave,
  onShowTour,
  requestedTab = null,
}: SidebarProps) {
  const [activeTab, setActiveTab] = React.useState<'nodes' | 'info'>('nodes');

  React.useEffect(() => {
    if (!requestedTab) return;
    setActiveTab(requestedTab);
  }, [requestedTab]);

  const onDragStart = (event: React.DragEvent, nodeType: string) => {
    event.dataTransfer.setData('application/workflow-node-type', nodeType);
    event.dataTransfer.effectAllowed = 'move';
  };

  const handleNewWorkflow = () => {
    const name = prompt('Workflow name:');
    if (!name) return;
    const id = name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    onSave({
      id,
      name,
      description: '',
      nodes: [],
      edges: [],
    });
  };

  return (
    <div className="sidebar" data-tour="sidebar">
      <div className="sidebar-tabs">
        <button
          className={`sidebar-tab ${activeTab === 'nodes' ? 'active' : ''}`}
          onClick={() => setActiveTab('nodes')}
          data-tour="nodes-tab"
        >
          Nodes
        </button>
        <button
          className={`sidebar-tab ${activeTab === 'info' ? 'active' : ''}`}
          onClick={() => setActiveTab('info')}
          data-tour="info-tab"
        >
          Info
        </button>
      </div>

      {activeTab === 'nodes' ? (
        <>
          <h3>Node Types</h3>
          <p style={{ fontSize: '11px', color: '#64748b', marginBottom: '8px' }}>
            Drag onto canvas
          </p>

          {Object.entries(nodeTypes).map(([type, info], index) => (
            <div
              key={type}
              className="node-type-card"
              draggable
              onDragStart={(e) => onDragStart(e, type)}
              data-tour={index === 0 ? 'node-palette-item' : undefined}
            >
              <div
                className="node-type-dot"
                style={{ backgroundColor: info.color }}
              />
              <span className="node-type-label">{info.label}</span>
            </div>
          ))}
        </>
      ) : (
        <div className="info-panel">
          <h3>How To Use</h3>
          <div className="info-card">
            <p>1. Drag node types onto the canvas.</p>
            <p>2. Connect nodes with edges.</p>
            <p>3. Click a node to edit prompt/config.</p>
            <p>4. Ask a question in Chat to run the flow.</p>
            <p>5. Open Logs to inspect metrics and replay runs.</p>
          </div>
          <button className="btn-primary" onClick={() => onShowTour?.()} data-tour="show-tour">
            Show Tour
          </button>
        </div>
      )}

      <div className="sidebar-actions">
        <button className="btn-secondary" onClick={handleNewWorkflow}>
          + New Workflow
        </button>
        {workflow && (
          <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'center' }}>
            {workflow.nodes.length} nodes, {workflow.edges.length} edges
          </div>
        )}
      </div>
    </div>
  );
}
